import React, { useRef, useLayoutEffect, useState } from 'react';
import type { ReactNode, RefObject } from 'react';

const PAGE_HEIGHT = 1125;

function flattenChildren(children: ReactNode): ReactNode[] {
  const result: ReactNode[] = [];
  React.Children.forEach(children, (child) => {
    if (React.isValidElement(child) && child.type === React.Fragment) {
      result.push(...flattenChildren(child.props.children));
    } else if (child !== null && child !== undefined && child !== false) {
      result.push(child);
    }
  });
  return result;
}

export function usePaginatedChildren(children: ReactNode): {
  pages: ReactNode[][];
  containerRef: RefObject<HTMLDivElement>;
  measuring: boolean;
  flatChildren?: ReactNode[];
} {
  const [pages, setPages] = useState<ReactNode[][]>([]);
  const [measuring, setMeasuring] = useState(true);
  const containerRef = useRef<HTMLDivElement>(null);

  const flatChildren = flattenChildren(children);

  useLayoutEffect(() => {
    if (!containerRef.current) return;
    const childNodes = Array.from(containerRef.current.childNodes) as HTMLElement[];
    let currentPage: ReactNode[] = [];
    let currentHeight = 0;
    const newPages: ReactNode[][] = [];

    childNodes.forEach((node, idx) => {
      const nodeHeight = node.offsetHeight;
      if (currentHeight + nodeHeight > PAGE_HEIGHT && currentPage.length > 0) {
        newPages.push(currentPage);
        currentPage = [];
        currentHeight = 0;
      }
      currentPage.push(flatChildren[idx]);
      currentHeight += nodeHeight;
    });

    if (currentPage.length > 0) {
      newPages.push(currentPage);
    }

    setPages(newPages);
    setMeasuring(false);
    // eslint-disable-next-line
  }, [children]);

  return { pages, containerRef, measuring, flatChildren };
}
